/**
 * CooperMembresManagement.js
 * Composant de gestion des membres pour les COOPER'S
 * Utilise le formulaire professionnel MembreCreationForm (copie conforme de DEVENIR MEMBRE)
 */
import React from 'react';

import { Button } from '../ui/button';

import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

import { Users, Send, CheckCircle, Calculator, Loader2, Shield, CreditCard, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _API = `${BACKEND_URL}/api`;

// V37.22 — Parser réutilisable pour l'import depuis presse-papier
const IMPORT_KEY_MAP = {
  'raison sociale': 'raison_sociale',
  'siret': 'siret',
  'prenom': 'prenom',
  'prénom': 'prenom',
  'nom': 'nom',
  'email': 'email',
  'telephone': 'telephone',
  'téléphone': 'telephone',
  'adresse': 'adresse',
  'code postal': 'code_postal',
  'ville': 'ville',
  'territoire': 'territoire',
};

// Champs critiques affichés en chips de validation (ordre = priorité)
const _IMPORT_CRITICAL_FIELDS = [
  { key: 'raison_sociale', label: 'Raison sociale' },
  { key: 'nom', label: 'Nom' },
  { key: 'prenom', label: 'Prénom' },
  { key: 'email', label: 'Email' },
  { key: 'telephone', label: 'Téléphone' },
  { key: 'territoire', label: 'Territoire' },
];

function parseImportText(text) {
  const out = {};
  (text || '').split(/\r?\n/).forEach((line) => {
    const idx = line.indexOf(':');
    if (idx <= 0) return;
    const rawKey = line.slice(0, idx).trim().toLowerCase();
    const value = line.slice(idx + 1).trim();
    if (!value) return;
    const mapped = IMPORT_KEY_MAP[rawKey];
    if (mapped) out[mapped] = value;
  });
  return out;
}

export default function CooperModals2({ ctx }) {
  const { ftpeLoading, handleCreateEstimation, handleCreateFTPEAdhesion, handleOpenSimulateur, newEstimation, selectedMembre, setNewEstimation, setShowEstimationModal, setShowFTPEModal, setShowSimulateurModal, showEstimationModal, showFTPEModal, showSimulateurModal, simulateursDisponibles } = ctx;
  return (
    <>
      {/* Modal Création Estimation */}
      <Dialog open={showEstimationModal} onOpenChange={setShowEstimationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-purple-600" />
              Nouvelle estimation
            </DialogTitle>
            <DialogDescription>
              {selectedMembre && (
                <>Pour {selectedMembre.prenom} {selectedMembre.nom}</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="est-titre">Titre de l&apos;estimation *</Label>
              <Input
                id="est-titre"
                value={newEstimation.titre}
                onChange={(e) => setNewEstimation({...newEstimation, titre: e.target.value})}
                placeholder="Estimation BATICO 2024"
              />
            </div>

            <div>
              <Label htmlFor="est-desc">Description</Label>
              <textarea
                id="est-desc"
                value={newEstimation.description}
                onChange={(e) => setNewEstimation({...newEstimation, description: e.target.value})}
                placeholder="Détails de l'estimation..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="est-montant">Montant de base (€) *</Label>
                <Input
                  id="est-montant"
                  type="number"
                  step="0.01"
                  value={newEstimation.montant_ht}
                  onChange={(e) => setNewEstimation({...newEstimation, montant_ht: e.target.value})}
                  placeholder="1500.00"
                />
              </div>
              <div>
                <Label htmlFor="est-tva">TVA (%)</Label>
                <Input
                  id="est-tva"
                  type="number"
                  value={newEstimation.taux_tva}
                  onChange={(e) => setNewEstimation({...newEstimation, taux_tva: parseFloat(e.target.value)})}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="est-validite">Validité (jours)</Label>
              <Input
                id="est-validite"
                type="number"
                value={newEstimation.validite_jours}
                onChange={(e) => setNewEstimation({...newEstimation, validite_jours: parseInt(e.target.value)})}
              />
            </div>

            {/* Aperçu calcul */}
            {newEstimation.montant_ht && (
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-800 font-medium mb-2">Aperçu</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Montant de base</span>
                    <span>{parseFloat(newEstimation.montant_ht).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span>TVA ({newEstimation.taux_tva}%)</span>
                    <span>{(parseFloat(newEstimation.montant_ht) * newEstimation.taux_tva / 100).toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between font-bold text-purple-700 border-t pt-1">
                    <span>Total Global</span>
                    <span>{(parseFloat(newEstimation.montant_ht) * (1 + newEstimation.taux_tva / 100)).toFixed(2)} €</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEstimationModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateEstimation} className="bg-purple-600 hover:bg-purple-700">
              <Send className="h-4 w-4 mr-2" />
              Créer l&apos;estimation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Adhésion FTPE */}
      <Dialog open={showFTPEModal} onOpenChange={setShowFTPEModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Adhésion FTPE Membre
            </DialogTitle>
            <DialogDescription>
              Créer une demande d&apos;adhésion à la Fédération TPE pour ce membre
            </DialogDescription>
          </DialogHeader>

          {selectedMembre && (
            <div className="space-y-4 py-4">
              {/* Info membre */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Informations du membre
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-blue-700">Nom:</span>
                    <p className="font-medium">{selectedMembre.prenom} {selectedMembre.nom}</p>
                  </div>
                  <div>
                    <span className="text-blue-700">Email:</span>
                    <p className="font-medium">{selectedMembre.email}</p>
                  </div>
                  {selectedMembre.entreprise && (
                    <div className="col-span-2">
                      <span className="text-blue-700">Entreprise:</span>
                      <p className="font-medium">{selectedMembre.entreprise}</p>
                    </div>
                  )}
                  {selectedMembre.territoire && (
                    <div>
                      <span className="text-blue-700">Territoire:</span>
                      <p className="font-medium">{selectedMembre.territoire}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Avantages FTPE */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Avantages de l&apos;adhésion FTPE
                </h4>
                <ul className="text-sm text-green-800 space-y-1">
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3" />
                    Garantie Fédération TPE Outremer
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3" />
                    Accès aux formations professionnelles
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3" />
                    Accompagnement personnalisé
                  </li>
                  <li className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3" />
                    Tarifs préférentiels sur les services
                  </li>
                </ul>
              </div>

              {/* Info paiement */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <CreditCard className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Une demande d&apos;adhésion sera créée. Le membre devra ensuite 
                    effectuer le paiement pour valider son adhésion.
                  </span>
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFTPEModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateFTPEAdhesion} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={ftpeLoading}
            >
              {ftpeLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création en cours...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Créer l'adhésion FTPE
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Sélection Simulateur */}
      <Dialog open={showSimulateurModal} onOpenChange={setShowSimulateurModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-amber-500" />
              Nouvelle simulation pour {selectedMembre?.prenom} {selectedMembre?.nom}
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un simulateur pour créer une nouvelle estimation. Les données du membre seront pré-remplies automatiquement.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {/* Info membre pré-rempli */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Données pré-remplies
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-blue-700">Nom:</span>
                  <p className="font-medium">{selectedMembre?.prenom} {selectedMembre?.nom}</p>
                </div>
                {selectedMembre?.entreprise && (
                  <div>
                    <span className="text-blue-700">Entreprise:</span>
                    <p className="font-medium">{selectedMembre?.entreprise}</p>
                  </div>
                )}
                {selectedMembre?.siret && (
                  <div>
                    <span className="text-blue-700">SIRET:</span>
                    <p className="font-medium">{selectedMembre?.siret}</p>
                  </div>
                )}
                {selectedMembre?.territoire && (
                  <div>
                    <span className="text-blue-700">Territoire:</span>
                    <p className="font-medium">{selectedMembre?.territoire}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Liste des simulateurs */}
            <h4 className="font-semibold text-gray-900 mb-3">Choisir un simulateur</h4>
            <div className="grid grid-cols-1 gap-3">
              {simulateursDisponibles.map((sim) => (
                <button
                  key={sim.id}
                  onClick={() => handleOpenSimulateur(sim)}
                  className={`w-full p-4 border-2 rounded-xl text-left transition-all hover:shadow-md hover:scale-[1.02] ${
                    sim.color === 'amber' ? 'border-amber-200 hover:border-amber-400 hover:bg-amber-50' :
                    sim.color === 'orange' ? 'border-orange-200 hover:border-orange-400 hover:bg-orange-50' :
                    sim.color === 'blue' ? 'border-blue-200 hover:border-blue-400 hover:bg-blue-50' :
                    'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${
                      sim.color === 'amber' ? 'bg-amber-100' :
                      sim.color === 'orange' ? 'bg-orange-100' :
                      sim.color === 'blue' ? 'bg-blue-100' :
                      'bg-gray-100'
                    }`}>
                      {sim.icon}
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900">{sim.nom}</h5>
                      <p className="text-sm text-gray-600">{sim.description}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSimulateurModal(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
